import React, { useCallback, useEffect, useState } from "react";
import {
  AddButton,
  CancelButton,
  SubmitButton,
  UpdateButton,
} from "../../../components/mui/Buttons/Buttons";
import { Form, FormikProvider, useFormik, FieldArray } from "formik";
import {
  Box,
  Stack,
  TextField,
  MenuItem,
  IconButton,
  Chip,
  Button,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import GlobalDialog from "../../../components/mui/Dialogue/GlobalDialog";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import * as Yup from "yup";
import {
  addSessionPreference,
  getOGAsessionPreference,
  getSessionPreferences,
  getSessionPreferencesNoLimit,
  getSessionTime,
  getSubjectEnrollmentId,
} from "../../../redux/features/croSlice";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { Icon } from "@iconify/react/dist/iconify.js";

// onAdded (optional): runs after a successful save (the retention workspace returns to itself)
const AddSessionPreference = ({ dataparams, courseenrollmentId, onAdded }) => {
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const {
    loading,
    subjectEnrollId,
    OGAsessionPreference,
    SessionPreferencesAll,
    SessionTime,
  } = useSelector((state) => state.cro);

  const [prefillValues, setPrefillValues] = useState(null);
  const [globalFromTime, setGlobalFromTime] = useState(null);
  const [globalToTime, setGlobalToTime] = useState(null);

  const ValidationSchema = Yup.object().shape({
    subject_enrollment_id: Yup.string().required(
      "Subject Enroll Id is required"
    ),
    sessions: Yup.array()
      .of(
        Yup.object().shape({
          week_day: Yup.number().required("Week day is required"),
          from_time: Yup.string().required("From time is required"),
          to_time: Yup.string().required("To time is required"),
        })
      )
      .min(1, "At least one session is required"),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: prefillValues || {
      subject_enrollment_id: "",
      sessions: [{ week_day: "", from_time: "", to_time: "" }],
    },
    validationSchema: ValidationSchema,
    onSubmit: (values) => {
      const payload = {
        data: values.sessions.map((session) => ({
          subject_enrollment_id: values.subject_enrollment_id,
          week_day: session.week_day,
          from_time: session.from_time,
          to_time: session.to_time,
        })),
      };

      dispatch(addSessionPreference(payload)).then((res) => {
        if (res.payload.status === "success") {
          enqueueSnackbar(res.payload.message, { variant: "success" });
          dispatch(getSessionPreferences(dataparams));
          formik.resetForm();
          setOpen(false);
          onAdded?.();
        } else {
          enqueueSnackbar(res.payload.message, { variant: "error" });
        }
      });
    },
  });

  const weekdays = [
    { id: 0, day: "Sunday" },
    { id: 1, day: "Monday" },
    { id: 2, day: "Tuesday" },
    { id: 3, day: "Wednesday" },
    { id: 4, day: "Thursday" },
    { id: 5, day: "Friday" },
    { id: 6, day: "Saturday" },
  ];

  useEffect(() => {
    if (open) {
      dispatch(getSubjectEnrollmentId(courseenrollmentId));
      dispatch(getOGAsessionPreference(courseenrollmentId));
    }
  }, [open]);

  useEffect(() => {
    if (formik.values.subject_enrollment_id) {
      dispatch(getSessionTime(formik.values.subject_enrollment_id));
    }
  }, [formik.values.subject_enrollment_id]);

  useEffect(() => {
    if (Array.isArray(SessionTime) && SessionTime.length > 0) {
      const firstSession = SessionTime[0];
      if (
        firstSession?.firstsession_starttime &&
        firstSession?.firstsession_endtime
      ) {
        setGlobalFromTime(
          dayjs(`2023-01-01T${firstSession.firstsession_starttime}`)
        );
        setGlobalToTime(
          dayjs(`2023-01-01T${firstSession.firstsession_endtime}`)
        );
      }
    }
  }, [SessionTime]);

  useEffect(() => {
    if (open) {
      const hasSessionPrefs = SessionPreferencesAll?.length > 0;
      const hasOGAPrefs = OGAsessionPreference?.length > 0;

      if (hasSessionPrefs) {
        const prefill = {
          subject_enrollment_id: "",
          sessions: SessionPreferencesAll.map((item) => ({
            week_day: item.week_day,
            from_time: item.from_time,
            to_time: item.to_time,
          })),
        };
        setPrefillValues(prefill);
      } else if (hasOGAPrefs) {
        const prefill = {
          subject_enrollment_id: "",
          sessions: OGAsessionPreference.map((item) => ({
            week_day: item.lpt_week_day,
            from_time: item.lpt_from_time,
            to_time: item.lpt_to_time,
          })),
        };
        setPrefillValues(prefill);
      }
    }
  }, [open, SessionPreferencesAll, OGAsessionPreference]);

  const gethandleAddItem = useCallback(
    (push) => () => {
      push({ week_day: "", from_time: "", to_time: "" });
    },
    []
  );

  return (
    <GlobalDialog
      title={"Add Preference"}
      open={open}
      setOpen={setOpen}
      fullWidth
      maxWidth={"md"}
      closeButton={true}
      popupButton={
        <AddButton size="medium" action={() => setOpen(true)}>
          Add Preference
        </AddButton>
      }
      content={
        <FormikProvider value={formik}>
          <Form onSubmit={formik.handleSubmit}>
            <Box>
              <Stack direction={"column"} spacing={2}>
                {/* Subject Enrollment Dropdown */}
                <TextField
                  select
                  fullWidth
                  label="Subject Enrollment"
                  name="subject_enrollment_id"
                  value={formik.values.subject_enrollment_id}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.subject_enrollment_id &&
                    Boolean(formik.errors.subject_enrollment_id)
                  }
                  helperText={
                    formik.touched.subject_enrollment_id &&
                    formik.errors.subject_enrollment_id
                  }
                >
                  {subjectEnrollId.data?.map((item) => (
                    <MenuItem
                      key={item.subject_enrollment_id}
                      value={item.subject_enrollment_id}
                    >
                      {item.course_name}-{item.subject_name}
                    </MenuItem>
                  ))}
                </TextField>
                {OGAsessionPreference?.length > 0 &&
                  SessionPreferencesAll?.length === 0 && (
                    <Chip
                      label="OGA Session Preferences"
                      variant="contained"
                      color="secondary"
                    />
                  )}
                {/* Dynamic Session Fields */}
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    alignItems="center"
                    sx={{
                      py: 2,
                    }}
                  >
                    <TimePicker
                      label="From Time"
                      value={globalFromTime}
                      onChange={(newValue) => setGlobalFromTime(newValue)}
                      renderInput={(params) => <TextField {...params} />}
                    />
                    <TimePicker
                      label="To Time"
                      value={globalToTime}
                      onChange={(newValue) => setGlobalToTime(newValue)}
                      renderInput={(params) => <TextField {...params} />}
                    />
                    <Button
                      size="medium"
                      color="primary"
                      variant="contained"
                      sx={{
                        gap: "5px",
                      }}
                      onClick={() => {
                        const formattedFrom =
                          dayjs(globalFromTime).format("HH:mm:ss");
                        const formattedTo =
                          dayjs(globalToTime).format("HH:mm:ss");
                        formik.setFieldValue(
                          "sessions",
                          formik.values.sessions.map((session) => ({
                            ...session,
                            from_time: formattedFrom,
                            to_time: formattedTo,
                          }))
                        );
                      }}
                    >
                      <Icon
                        icon={"teenyicons:tick-circle-solid"}
                        width={20}
                        height={20}
                      />
                      Apply Time to All
                    </Button>
                  </Stack>
                </LocalizationProvider>

                <FieldArray name="sessions">
                  {({ push, remove }) => (
                    <>
                      {formik.values.sessions.map((session, index) => (
                        <Stack
                          key={index}
                          direction={{
                            xs: "column",
                            sm: "row",
                            md: "row",
                          }}
                          spacing={2}
                          alignItems="center"
                        >
                          {/* Weekday Select */}
                          <TextField
                            select
                            fullWidth
                            label="Weekday"
                            name={`sessions.${index}.week_day`}
                            value={session.week_day}
                            onChange={formik.handleChange}
                            error={
                              formik.touched.sessions?.[index]?.week_day &&
                              Boolean(formik.errors.sessions?.[index]?.week_day)
                            }
                            helperText={
                              formik.touched.sessions?.[index]?.week_day &&
                              formik.errors.sessions?.[index]?.week_day
                            }
                          >
                            {weekdays.map((day) => (
                              <MenuItem key={day.id} value={day.id}>
                                {day.day}
                              </MenuItem>
                            ))}
                          </TextField>

                          {/* From Time Picker */}
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <TimePicker
                              sx={{ width: "100%" }}
                              label="From Time"
                              value={
                                session.from_time
                                  ? dayjs(`2023-01-01T${session.from_time}`)
                                  : null
                              }
                              onChange={(newValue) => {
                                formik.setFieldValue(
                                  `sessions.${index}.from_time`,
                                  dayjs(newValue).format("HH:mm:ss")
                                );
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  fullWidth
                                  onKeyDown={(e) => e.preventDefault()}
                                  error={
                                    formik.touched.sessions?.[index]
                                      ?.from_time &&
                                    Boolean(
                                      formik.errors.sessions?.[index]?.from_time
                                    )
                                  }
                                  helperText={
                                    formik.touched.sessions?.[index]
                                      ?.from_time &&
                                    formik.errors.sessions?.[index]?.from_time
                                  }
                                />
                              )}
                            />
                          </LocalizationProvider>

                          {/* To Time Picker */}
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <TimePicker
                              sx={{ width: "100%" }}
                              label="To Time"
                              value={
                                session.to_time
                                  ? dayjs(`2023-01-01T${session.to_time}`)
                                  : null
                              }
                              onChange={(newValue) => {
                                formik.setFieldValue(
                                  `sessions.${index}.to_time`,
                                  dayjs(newValue).format("HH:mm:ss")
                                );
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  fullWidth
                                  onKeyDown={(e) => e.preventDefault()}
                                  error={
                                    formik.touched.sessions?.[index]?.to_time &&
                                    Boolean(
                                      formik.errors.sessions?.[index]?.to_time
                                    )
                                  }
                                  helperText={
                                    formik.touched.sessions?.[index]?.to_time &&
                                    formik.errors.sessions?.[index]?.to_time
                                  }
                                />
                              )}
                            />
                          </LocalizationProvider>

                          {/* Remove Session Button */}
                          {formik.values.sessions.length > 1 && (
                            <IconButton onClick={() => remove(index)}>
                              <Delete />
                            </IconButton>
                          )}
                        </Stack>
                      ))}

                      {/* Add More Session Button */}
                      <AddButton size="medium" action={gethandleAddItem(push)}>
                        Add Session
                      </AddButton>
                    </>
                  )}
                </FieldArray>
              </Stack>

              {/* Action Buttons */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 2 }}>
                <Stack direction={"row"} spacing={1}>
                  <CancelButton size="small" action={() => setOpen(false)}>
                    Cancel
                  </CancelButton>
                  <SubmitButton disabled={loading} size="small" type="submit">
                    {loading ? "Please wait..." : "Save"}
                  </SubmitButton>
                </Stack>
              </Box>
            </Box>
          </Form>
        </FormikProvider>
      }
    />
  );
};

export default AddSessionPreference;
